import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { uuidv7 } from "@huayruro/shared";
import { dbLocal } from "../lib/db-local";
import { encolar } from "../lib/cola";
import { flushAhora } from "../lib/useSyncPos";
import { useCatalogoVenta } from "../lib/useCatalogoVenta";
import { useCarrito, totalLineaCent } from "../lib/useCarrito";
import { useBarcodeScanner } from "../lib/useBarcodeScanner";
import { imprimirGuia, type GuiaVenta } from "../lib/impresora";
import { solesCent } from "../lib/money";
import { Buscador } from "../components/Buscador";
import { Carrito } from "../components/Carrito";
import { CobrarModal } from "../components/CobrarModal";
import { QuiebreModal } from "../components/QuiebreModal";
import { PorCrearModal } from "../components/PorCrearModal";
import { abrirCatalogoCon, separarCodigoONombre } from "../lib/abrir-catalogo";
import { SenalesBandeja } from "../components/SenalesBandeja";
import { ClienteModal } from "../components/ClienteModal";
import { PanelSeguimiento } from "../components/PanelSeguimiento";
import { TarjetaSugerencia } from "../components/TarjetaSugerencia";
import { PanelCopiloto } from "../components/PanelCopiloto";
import { TarjetaRostro } from "../components/TarjetaRostro";
import { RegistrarCaraModal } from "../components/RegistrarCaraModal";
import { useSugerenciasRostro } from "../lib/rostro";
import { useCopiloto } from "../lib/useCopiloto";
import { TratamientoModal, type ItemVendido } from "../components/TratamientoModal";
import { useSenales } from "../lib/useSenales";
import { useSeguimientoMostrador } from "../lib/useSeguimiento";
import { useSugerencia } from "../lib/useSugerencia";
import { useMasAngostoQue, UMBRAL_FILA_PX } from "../lib/pantalla";
import { nombreCorto, panelCliente, type Cliente } from "../lib/clientes";
import type { MetodoPago, ProductoVenta, SesionActiva } from "../lib/tipos";

// Motivos de «Cajón» (abrirlo sin vender). El server ya aceptaba el motivo y la pantalla nunca lo
// mandaba; elegirlo es a la vez la confirmación: un toque por curiosidad ya no deja un registro que
// alimenta un indicador de Casos (tabla 4.2 del tramo 02, CH-16).
const MOTIVOS_CAJON = ["Dar vuelto", "Sencillo", "Revisar"] as const;

type ToastState =
  | { kind: "none" }
  | { kind: "ok"; message: string }
  | { kind: "warn"; message: string }
  | { kind: "error"; message: string };

// Lo que queda pendiente de registrar apenas se cobró: el cliente al que se le vendió y qué se llevó.
type SeguimientoPorRegistrar = { clienteId: string; nombre: string; items: ItemVendido[]; clientUuid: string };

// `barraBotica`: el «Atiendes en … · Cambiar de botica» del Dueño. En escritorio va al final de la fila de
// acciones (sin franja propia: Kevin, 15-oct-2026, la pantalla de venta no debe necesitar scroll); en el
// celular, arriba de la fila como antes.
export function Mostrador({ sesion, barraBotica }: { sesion: SesionActiva; barraBotica?: ((enFila: boolean) => ReactNode) | undefined }) {
  const catalogo = useCatalogoVenta(dbLocal);
  const carrito = useCarrito();
  const [showCobrar, setShowCobrar] = useState(false);
  const [showQuiebre, setShowQuiebre] = useState(false);
  const [showPorCrear, setShowPorCrear] = useState(false);
  const [showSenales, setShowSenales] = useState(false);
  const [showCliente, setShowCliente] = useState(false);
  const [showSeguimiento, setShowSeguimiento] = useState(false);
  const [showCajon, setShowCajon] = useState(false);
  const [showMas, setShowMas] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [cobrando, setCobrando] = useState(false);
  const [toast, setToast] = useState<ToastState>({ kind: "none" });
  const [ultimaGuia, setUltimaGuia] = useState<GuiaVenta | null>(null);

  // P1: cliente asignado a la venta en curso (§12 / expansión A1). Se limpia al cobrar: la siguiente
  // persona en la cola es otra. El id vive acá y NO en el carrito porque también se puede asignar sin
  // nada en el carrito (para consultar su seguimiento antes de vender).
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [clienteIdPanel, setClienteIdPanel] = useState<string | null>(null);
  const [recargaPanel, setRecargaPanel] = useState(0);
  const [porRegistrar, setPorRegistrar] = useState<SeguimientoPorRegistrar | null>(null);

  const conSucursal = !!sesion.sucursal;
  // Señales del audio (B10.2): solo si el operador tiene sucursal (el super no cobra en el mostrador).
  const { senales, confirmar: confirmarSenal, descartar: descartarSenal } = useSenales(conSucursal);
  const seguimiento = useSeguimientoMostrador(conSucursal);
  // A4 (venta cruzada): motor LOCAL sobre las reglas cacheadas. Máximo UNA tarjeta por venta.
  const sugerencia = useSugerencia(conSucursal);
  // Clientes por cara (tramo 10): lo que la webcam del mostrador reconoció. Solo SUGIERE; quien atiende confirma.
  const rostro = useSugerenciasRostro(conSucursal);
  const [registrarCara, setRegistrarCara] = useState<{ id: string; nombre: string } | null>(null);

  const flash = useCallback((t: ToastState, ms = 2200) => {
    setToast(t);
    if (t.kind !== "none") setTimeout(() => setToast({ kind: "none" }), ms);
  }, []);

  const alError = useCallback((m: string) => flash({ kind: "error", message: m }, 3500), [flash]);

  const agregar = useCallback(
    (p: ProductoVenta) => {
      carrito.agregar(p);
      flash({ kind: "warn", message: `Agregado: ${p.nombre}` }, 1000);
      // El consejo se evalúa con el producto que ACABA de entrar; el carrito de `carrito.items`
      // todavía no lo tiene (el estado se aplica en el próximo render), así que se suma a mano para
      // que la regla no pueda sugerir algo que ya está en la venta.
      sugerencia.evaluar(
        { producto_id: p.producto_id, categoria: p.categoria, principio_activo: p.principio_activo },
        [...carrito.items.map((it) => it.producto.producto_id), p.producto_id],
        catalogo.stockPorProducto,
        catalogo.porProductoId,
      );
    },
    [carrito, flash, sugerencia, catalogo],
  );

  // Un tap la agrega al carrito (y ese producto ya no puede volver a dispararse: `yaMostrada` cerró
  // el cupo de la venta). El otro tap la descarta. Los dos se miden.
  const aceptarSugerencia = useCallback(() => {
    const p = sugerencia.viva?.producto;
    if (!p) return;
    carrito.agregar(p);
    sugerencia.responder("aceptada");
    flash({ kind: "ok", message: `Agregado: ${p.nombre}` }, 1500);
  }, [sugerencia, carrito, flash]);

  // Lector de códigos de barras (HID): GTIN → producto de la cache local.
  useBarcodeScanner(
    useCallback(
      (gtin: string) => {
        const e = catalogo.escanear(gtin);
        if (e.tipo === "ok") agregar(e.producto);
        else if (e.tipo === "sin_precio") {
          // Kevin, 30-sep: una caja sin precio aquí ya no se cobra como una unidad. No se agrega nada.
          flash({ kind: "warn", message: `${e.presentacion} de ${e.nombre}: no tiene precio en esta botica. No se agregó.` }, 5000);
        } else {
          // RU-1 (tramo 07): el código queda en el buscador, así «Sin resultados.» ofrece «Sí lo tengo»,
          // «No hay» y (al Encargado) «Crear producto» con ese código ya puesto.
          setBusqueda(gtin);
          flash({ kind: "warn", message: `Código ${gtin} no encontrado` });
        }
      },
      [catalogo, agregar, flash],
    ),
  );

  // El dueño también vende (Kevin, 8-oct-2026), pero solo con una botica elegida: App le arma la sesión
  // con esa botica. Sin botica no se cobra (la venta no sabría de qué stock descontar).
  const puedeVender = sesion.usuario.rol !== "super_admin" || !!sesion.sucursal;
  // Crear la ficha de un producto es de quien maneja el Catálogo: el Encargado y el Dueño.
  const esEncargado = sesion.usuario.rol === "admin_sucursal" || sesion.usuario.rol === "super_admin";

  // Copiloto (etapa 1, Kevin 16-oct-2026): siempre a la vista; preguntas de recordatorio y
  // recomendaciones de uso de lo que hay en el carrito, por principio activo.
  const productosCopiloto = useMemo(
    () => carrito.items.map((it) => ({ producto_id: it.producto.producto_id, nombre: it.producto.nombre, principio_activo: it.producto.principio_activo })),
    [carrito.items],
  );
  const copiloto = useCopiloto(productosCopiloto);
  const panelCopiloto = (variante: "columna" | "franja") => (
    <PanelCopiloto
      variante={variante}
      preguntas={copiloto.preguntas}
      recomendaciones={copiloto.recomendaciones}
      conProductos={carrito.items.length > 0}
      marcadas={copiloto.marcadas}
      onMarcar={copiloto.onMarcar}
      puedeConfigurar={esEncargado}
      arriba={rostro.sugerencias[0] ? <TarjetaRostro s={rostro.sugerencias[0]} onResponder={(si) => void responderRostro(rostro.sugerencias[0]!, si)} /> : undefined}
    />
  );

  // Badge del panel: a cuántos ya les tocaba y cuántos cumplen HOY (lo accionable de este minuto).
  const porSeguirHoy = useMemo(() => seguimiento.pendientes.filter((p) => p.dias_de_atraso >= 0).length, [seguimiento.pendientes]);
  const cumplenHoy = useMemo(() => seguimiento.cumpleanos.filter((c) => c.dias_para === 0).length, [seguimiento.cumpleanos]);
  const avisos = porSeguirHoy + cumplenHoy;

  const asignar = useCallback((c: Cliente) => {
    setCliente(c);
    setClienteIdPanel(c.id);
    setShowCliente(false);
  }, []);

  // «¿Es Rosa?» → Sí: cuenta como que entró y, si es de esta botica, se le asigna como cliente de la venta.
  async function responderRostro(s: { id: string; nombre: string }, si: boolean) {
    try {
      const r = await rostro.responder(s.id, si);
      if (!si) return;
      if (!r.misma_botica) {
        flash({ kind: "warn", message: `${s.nombre} es cliente de otra botica: su ficha está allá.` }, 3500);
        return;
      }
      const p = await panelCliente(r.cliente_id);
      asignar(p.cliente);
    } catch (e) {
      flash({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  const quitarCliente = useCallback(() => {
    setCliente(null);
    setClienteIdPanel(null);
  }, []);

  // A4: el carrito vacío = atención terminada, se haya cobrado o no. Si la persona se fue sin
  // comprar, lo mostrado y lo rechazado se manda igual — contar solo las ventas cerradas dejaría la
  // conversión inflada. Tras cobrar el buffer ya se vació, así que acá no vuelve a disparar.
  const cerrarSugerencia = sugerencia.cerrar;
  const carritoVacio = carrito.items.length === 0;
  useEffect(() => {
    if (carritoVacio) void cerrarSugerencia(null);
  }, [carritoVacio, cerrarSugerencia]);

  async function handleCobrar(metodo: MetodoPago, efectivoRecibidoCent: number | null, recetasVistas: boolean[] = []) {
    if (!puedeVender) {
      flash({ kind: "error", message: "Elige en qué botica estás atendiendo antes de cobrar" });
      return;
    }
    setCobrando(true);
    try {
      const clientUuid = uuidv7();
      const nowIso = new Date().toISOString();
      const itemsVendidos: ItemVendido[] = carrito.items.map((it) => ({
        producto_id: it.producto.producto_id,
        nombre: it.producto.nombre,
      }));
      const clienteVenta = cliente;
      // Escribir SIEMPRE primero en la cola (§9); la UI confirma e imprime al toque desde datos locales.
      await encolar(dbLocal, "venta", {
        client_uuid: clientUuid,
        metodo_pago: metodo,
        items: carrito.items.map((it, i) => ({
          producto_id: it.producto.producto_id,
          presentacion_id: it.producto.presentacion_id,
          cantidad: it.cantidad,
          precio_sin_igv_unitario_dm: it.producto.precio_sin_igv_dm,
          // Freno de receta: la casilla «Vi la receta» de ese ítem en el cobro.
          ...(recetasVistas[i] ? { receta_vista: true } : {}),
        })),
        fecha_hora_cliente: nowIso,
        // Δ2: desde cuándo se está atendiendo a esta persona (primer ítem del carrito). Sin esto el
        // espejo operativo no puede calcular el tiempo de servicio y lo reportaba siempre vacío.
        atencion_inicio: carrito.atencionInicio,
        // A1: la venta queda con nombre y apellido si se identificó al cliente. El server valida que
        // sea de ESTA botica antes de insertar (404 si no) — acá no se manda nada más.
        cliente_id: clienteVenta?.id ?? null,
      }, {
        // D11: si el servidor la rechaza, la lista de este equipo muestra qué se cobró.
        items: carrito.items.map((it) => ({ nombre: it.producto.nombre, presentacion: it.producto.presentacion_nombre, cantidad: it.cantidad })),
        total_cent: carrito.totales.total_cent,
      });
      flushAhora();

      const guia: GuiaVenta = {
        sucursalNombre: sesion.sucursal?.nombre ?? "Botica Huayruro",
        sucursalDireccion: sesion.sucursal?.direccion ?? null,
        fechaHora: nowIso,
        clientUuid,
        items: carrito.items.map((it) => ({
          nombre: it.producto.nombre,
          presentacion: it.producto.presentacion_nombre,
          cantidad: it.cantidad,
          totalCent: totalLineaCent(it),
        })),
        subtotalSinIgvCent: carrito.totales.subtotal_sin_igv_cent,
        igvTotalCent: carrito.totales.igv_total_cent,
        totalCent: carrito.totales.total_cent,
        metodoPago: metodo,
        efectivoRecibidoCent: efectivoRecibidoCent,
        vueltoCent: efectivoRecibidoCent != null ? efectivoRecibidoCent - carrito.totales.total_cent : null,
      };
      setUltimaGuia(guia);
      void imprimirGuia(guia);

      // A4: la atención se cierra CON el client_uuid de la venta y DESPUÉS de encolarla — la cola es
      // FIFO, así que el server ya tiene la venta cuando llegan los eventos y puede enlazar el
      // `venta_id`. Sin ese enlace la tabla de conversión no podría medir soles reales.
      await sugerencia.cerrar(clientUuid);

      flash({ kind: "ok", message: `Venta ${clientUuid.slice(-6)} · ${solesCent(guia.totalCent)}` }, 3500);
      carrito.limpiar();
      setShowCobrar(false);
      quitarCliente();

      // Con cliente identificado, el seguimiento se ofrece en el acto: es el momento en que quien
      // atiende todavía se acuerda de para quién era y qué le dijo.
      if (clienteVenta && itemsVendidos.length > 0) {
        setPorRegistrar({ clienteId: clienteVenta.id, nombre: nombreCorto(clienteVenta), items: itemsVendidos, clientUuid });
      }
    } catch (e) {
      flash({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setCobrando(false);
    }
  }

  // Apertura declarada del cajón sin venta (Δ3 'no_sale'): para dar vuelto / revisar. Offline vía la
  // misma cola. Alimenta el indicador EBR `cajon_sin_venta` (frecuencia por operador vs promedio).
  async function abrirCajon(motivo: (typeof MOTIVOS_CAJON)[number]) {
    setShowCajon(false);
    await encolar(dbLocal, "no_sale", { fecha_hora_cliente: new Date().toISOString(), motivo });
    flushAhora();
    flash({ kind: "ok", message: `Cajón abierto: ${motivo.toLowerCase()}` }, 2500);
  }

  // R-D (tramo 03): todo control del celular mide 44 px o más. En el celular las acciones van en UNA
  // fila, «Faltante · Cliente · Más», con Seguimiento, Cajón y Reimprimir dentro de «Más» (Kevin lo
  // eligió el 27-sep-2026 mirando capturas: el buscador sube del píxel 290 al 238 de 844). Desde
  // 768 px entran las cinco en una fila y se muestran siempre (el e2e de «Reimprimir» corre a 1440 px).
  const conMas = useMasAngostoQue(UMBRAL_FILA_PX);
  const accion = "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-[9px] border px-3 text-[13.5px] font-medium";
  const accionNeutra = `${accion} bg-card border-line-input text-ink-emph hover:bg-hover-btn`;

  const panel = (
    <PanelSeguimiento
      clienteId={clienteIdPanel}
      pendientes={seguimiento.pendientes}
      cumpleanos={seguimiento.cumpleanos}
      recargaToken={recargaPanel}
      onAsignarId={(id) => {
        setClienteIdPanel(id);
        setShowSeguimiento(true);
      }}
      onQuitar={quitarCliente}
      onRegistrarCara={(id, nombre) => setRegistrarCara({ id, nombre })}
      onRegistrarSeguimiento={(id, nombre) =>
        // Sin venta recién cobrada: se ofrece lo que haya en el carrito y, si no hay nada, se escribe
        // a mano ("otra cosa"). Sirve para registrar lo que se llevó alguien que ya pasó por caja.
        setPorRegistrar({
          clienteId: id,
          nombre,
          items: carrito.items.map((it) => ({ producto_id: it.producto.producto_id, nombre: it.producto.nombre })),
          clientUuid: "",
        })
      }
      onCambio={seguimiento.recargar}
      onError={alError}
    />
  );

  // Botones de la fila de acciones. «Faltante» va primero: es la acción del mostrador que más se usa
  // después de vender. Su texto ámbar va sobre la tarjeta blanca (5,0:1): sobre el ámbar claro de
  // antes daba 4,41:1, bajo el mínimo legible.
  const botonFaltante = (
    <button onClick={() => setShowQuiebre(true)} className={`${accion} bg-card border-warn-dot/50 text-warn font-semibold hover:bg-warn-soft`}>
      Faltante
    </button>
  );
  const botonCliente =
    conSucursal &&
    (cliente ? (
      <span className={`${accion} min-w-0 bg-ok-soft border-ok/30 text-ok font-semibold`}>
        <span className="truncate">👤 {nombreCorto(cliente)}</span>
        <button onClick={quitarCliente} aria-label="Quitar cliente" className="-mr-1.5 inline-flex h-11 w-9 flex-none items-center justify-center text-ok/80 hover:text-ok">
          ✕
        </button>
      </span>
    ) : (
      <button onClick={() => setShowCliente(true)} className={accionNeutra} title="Identificar al cliente de esta venta">
        👤 Cliente
      </button>
    ));
  const botonSeguimiento = conSucursal && (
    <button
      onClick={() => setShowSeguimiento(true)}
      className={`xl:hidden ${accion} ${avisos > 0 ? "bg-info-soft border-info/30 text-info-ink" : "bg-card border-line-input text-ink-emph hover:bg-hover-btn"}`}
      title="A quién le toca seguimiento y quién cumple años"
    >
      🗓️ Seguimiento{avisos > 0 ? ` ${avisos}` : ""}
    </button>
  );
  const botonCajon = (
    <button onClick={() => setShowCajon(true)} className={accionNeutra} title="Registrar apertura del cajón sin una venta (dar vuelto, sencillo, revisar)">
      🔓 Cajón
    </button>
  );
  const botonReimprimir = (
    <button
      onClick={() => ultimaGuia && void imprimirGuia({ ...ultimaGuia, reimpresion: true })}
      disabled={!ultimaGuia}
      className={`${accionNeutra} disabled:opacity-40`}
    >
      Reimprimir
    </button>
  );
  // Señales del oído: con palabra («Oído 3») y no solo el ícono — un botón sin palabra no se entiende.
  // Titila solo el ícono: con el botón entero titilando, la mitad del tiempo el texto se veía apagado.
  const botonOido = senales.length > 0 && (
    <button
      onClick={() => setShowSenales(true)}
      className={`${accion} bg-info-soft border-info/30 text-info-ink`}
      title="Señales detectadas por el audio del mostrador"
    >
      <span aria-hidden="true" className="animate-pulse-dot">🎙️</span> Oído {senales.length}
    </button>
  );

  return (
    // En el celular la barra fija de «Cobrar» tapa el último pedazo de la página: se le deja su sitio.
    <div className="flex flex-col max-md:pb-[88px] md:h-full md:min-h-0">
      <div className="mb-3 flex flex-col gap-1.5">
        {conMas ? (
          <>
            {barraBotica?.(false)}
            {/* Una sola fila de posiciones iguales, sin partir el texto. Con cliente identificado, su
                nombre va en una fila propia: en una casilla de 86 px no se leía (captura del tramo 03). */}
            <div
              className="grid gap-2 [&>*]:whitespace-nowrap [&>*]:px-1.5"
              style={{ gridTemplateColumns: `repeat(${2 + (cliente ? 0 : 1) + (botonOido ? 1 : 0)}, minmax(0, 1fr))` }}
            >
              {botonFaltante}
              {!cliente && botonCliente}
              <button
                onClick={() => setShowMas(true)}
                aria-haspopup="dialog"
                className={`${accion} ${avisos > 0 ? "bg-info-soft border-info/30 text-info-ink" : "bg-card border-line-input text-ink-emph hover:bg-hover-btn"}`}
              >
                Más{avisos > 0 ? ` · ${avisos}` : ""}
              </button>
              {botonOido}
            </div>
            {cliente && botonCliente}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {botonFaltante}
            {botonCliente}
            {botonSeguimiento}
            {botonOido}
            {botonCajon}
            {botonReimprimir}
            {barraBotica && (
              <div className="ml-auto" data-testid="barra-botica-en-fila">
                {barraBotica(true)}
              </div>
            )}
          </div>
        )}
        <p className="text-[11.5px] text-ink-3 tabular-nums">
          {catalogo.listo ? `${catalogo.total} productos en cache` : "Cargando catálogo..."}
        </p>
      </div>

      {/* Bajo 1280 px no hay tercera columna: el copiloto va como franja sobre la búsqueda. */}
      {conSucursal && <div className="xl:hidden">{panelCopiloto("franja")}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:flex-1 md:min-h-0 md:grid-rows-[minmax(0,1fr)]">
        <Buscador
          buscar={catalogo.buscar}
          onAgregar={agregar}
          query={busqueda}
          onQuery={setBusqueda}
          onSiLoTengo={() => setShowPorCrear(true)}
          onAnotarFaltante={() => setShowQuiebre(true)}
          onCrearProducto={esEncargado ? () => abrirCatalogoCon({ tipo: "alta", ...separarCodigoONombre(busqueda), sucursalId: sesion.sucursal?.id ?? null }) : undefined}
        />
        <Carrito
          items={carrito.items}
          totales={carrito.totales}
          onSetCantidad={carrito.setCantidad}
          onQuitar={carrito.quitar}
          onCobrar={() => setShowCobrar(true)}
          onLimpiar={carrito.limpiar}
          cobrando={cobrando}
          sugerencia={
            sugerencia.viva && (
              <TarjetaSugerencia
                sugerencia={sugerencia.viva}
                onAgregar={aceptarSugerencia}
                onDescartar={() => sugerencia.responder("rechazada")}
              />
            )
          }
        />
        {/* Panel lateral de Seguimiento: columna propia en escritorio; en el celular se abre a pantalla
            completa desde el botón, para no robarle sitio al carrito en hora punta. */}
        {conSucursal && (
          <aside className="hidden xl:flex min-h-0 flex-col gap-3 overflow-y-auto rounded-[14px] border border-line bg-card p-4">
            {panelCopiloto("columna")}
            <div className="border-t border-line-row pt-3">{panel}</div>
          </aside>
        )}
      </div>

      {showCobrar && (
        <CobrarModal
          totalCent={carrito.totales.total_cent}
          items={carrito.items.map((it) => ({ nombre: it.producto.nombre, cantidad: it.cantidad, requiere_receta: it.producto.requiere_receta }))}
          onConfirmar={handleCobrar}
          onCancelar={() => setShowCobrar(false)}
        />
      )}
      {showCajon && (
        <HojaAcciones titulo="¿Para qué abres el cajón?" subtitulo="Queda anotado sin venta. Elegir el motivo es la confirmación." onCerrar={() => setShowCajon(false)}>
          {MOTIVOS_CAJON.map((m) => (
            <button key={m} onClick={() => void abrirCajon(m)} className="min-h-[48px] w-full rounded-[9px] border border-line-input bg-card px-4 text-left text-[14px] font-semibold text-ink hover:bg-hover-btn">
              {m}
            </button>
          ))}
        </HojaAcciones>
      )}
      {showMas && (
        <HojaAcciones titulo="Más acciones de la venta" onCerrar={() => setShowMas(false)}>
          {conSucursal && (
            <button
              onClick={() => {
                setShowMas(false);
                setShowSeguimiento(true);
              }}
              className="min-h-[48px] w-full rounded-[9px] border border-line-input bg-card px-4 text-left text-[14px] font-semibold text-ink hover:bg-hover-btn"
            >
              🗓️ Seguimiento{avisos > 0 ? ` · ${avisos}` : ""}
              <span className="block text-[12px] font-normal text-ink-2">A quién le toca y quién cumple años</span>
            </button>
          )}
          <button
            onClick={() => {
              setShowMas(false);
              setShowCajon(true);
            }}
            className="min-h-[48px] w-full rounded-[9px] border border-line-input bg-card px-4 text-left text-[14px] font-semibold text-ink hover:bg-hover-btn"
          >
            🔓 Cajón
            <span className="block text-[12px] font-normal text-ink-2">Abrirlo sin vender: dar vuelto, sencillo o revisar</span>
          </button>
          <button
            onClick={() => {
              setShowMas(false);
              if (ultimaGuia) void imprimirGuia({ ...ultimaGuia, reimpresion: true });
            }}
            disabled={!ultimaGuia}
            className="min-h-[48px] w-full rounded-[9px] border border-line-input bg-card px-4 text-left text-[14px] font-semibold text-ink hover:bg-hover-btn disabled:opacity-40"
          >
            Reimprimir
            <span className="block text-[12px] font-normal text-ink-2">La guía de la última venta</span>
          </button>
        </HojaAcciones>
      )}
      {showCliente && <ClienteModal onAsignar={asignar} onCerrar={() => setShowCliente(false)} />}
      {registrarCara && <RegistrarCaraModal clienteId={registrarCara.id} nombre={registrarCara.nombre} onCerrar={() => setRegistrarCara(null)} />}
      {showSeguimiento && conSucursal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-surface/40 backdrop-blur p-0 sm:p-4 xl:hidden">
          <div className="w-full sm:max-w-md bg-card border border-line rounded-t-[14px] sm:rounded-[14px] p-5 shadow-[0_10px_30px_rgba(36,29,26,0.25)] max-h-[85vh] flex flex-col">
            <div className="flex justify-end">
              <button onClick={() => setShowSeguimiento(false)} className="inline-flex min-h-11 items-center px-2 text-sm underline text-ink-2">
                cerrar
              </button>
            </div>
            <div className="min-h-0 flex-1">{panel}</div>
          </div>
        </div>
      )}
      {porRegistrar && (
        <TratamientoModal
          clienteId={porRegistrar.clienteId}
          clienteNombre={porRegistrar.nombre}
          items={porRegistrar.items}
          clientUuidVenta={porRegistrar.clientUuid || null}
          onListo={(m) => {
            flash({ kind: "ok", message: m }, 3000);
            setPorRegistrar(null);
            setRecargaPanel((n) => n + 1);
            seguimiento.recargar();
          }}
          onCerrar={() => setPorRegistrar(null)}
        />
      )}
      {showPorCrear && (
        <PorCrearModal
          inicial={busqueda}
          onListo={(m) => {
            flash({ kind: "ok", message: m });
            setShowPorCrear(false);
            setBusqueda("");
          }}
          onCancelar={() => setShowPorCrear(false)}
        />
      )}
      {showQuiebre && (
        <QuiebreModal
          buscar={catalogo.buscar}
          inicial={busqueda}
          onListo={(m) => {
            flash({ kind: "ok", message: m });
            setShowQuiebre(false);
          }}
          onCancelar={() => setShowQuiebre(false)}
        />
      )}
      {showSenales && (
        <SenalesBandeja
          senales={senales}
          onConfirmar={confirmarSenal}
          onDescartar={descartarSenal}
          onListo={(m) => flash({ kind: "ok", message: m })}
          onError={(m) => flash({ kind: "error", message: m })}
          onCerrar={() => setShowSenales(false)}
        />
      )}

      {toast.kind !== "none" && <Toast state={toast} />}
    </div>
  );
}

function Toast({ state }: { state: Exclude<ToastState, { kind: "none" }> }) {
  // Arriba y no abajo: abajo tapaba el total y el borde de «Cobrar», que es el número que quien
  // atiende dice en voz alta (tabla 4.2 del tramo 02). Colores de tokens del contrato.
  const cls =
    state.kind === "ok"
      ? "bg-ok-soft text-ok"
      : state.kind === "warn"
        ? "bg-warn-soft text-warn"
        : "bg-accent-soft text-accent-ink";
  return (
    <div role="status" className="pointer-events-none fixed top-3 left-1/2 z-40 w-max max-w-[calc(100vw-24px)] -translate-x-1/2">
      <div className={`px-4 py-3 rounded-[11px] font-medium border border-line shadow-[0_10px_30px_rgba(36,29,26,0.25)] ${cls}`}>
        {state.kind === "ok" ? "✓ " : state.kind === "error" ? "⚠ " : ""}
        {state.message}
      </div>
    </div>
  );
}

// Hoja de acciones del mostrador (Cajón, Más): abajo en el celular, al centro en pantallas anchas.
function HojaAcciones({ titulo, subtitulo, onCerrar, children }: { titulo: string; subtitulo?: string; onCerrar: () => void; children: ReactNode }) {
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [onCerrar]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-surface/40 p-0 sm:p-4" onClick={onCerrar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="flex w-full flex-col gap-2 rounded-t-[14px] border border-line bg-card p-4 pb-[max(16px,env(safe-area-inset-bottom))] shadow-[0_10px_30px_rgba(36,29,26,0.25)] sm:max-w-sm sm:rounded-[14px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-[16px] font-bold text-ink">{titulo}</h2>
            {subtitulo && <p className="mt-0.5 text-[12.5px] text-ink-2">{subtitulo}</p>}
          </div>
          <button onClick={onCerrar} className="min-h-[44px] flex-none rounded-[9px] px-3 text-[13px] font-medium text-ink-2 hover:bg-hover-btn">
            Cancelar
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
